import type { ReactNode } from "react"
import { Icon } from "./icons.tsx"

/** 검색·필터 한 줄. 액션은 오른쪽으로 밀리고, 좁으면 다음 줄로 내려간다. */
export function FilterBar({
  children,
  actions,
}: {
  children: ReactNode
  actions?: ReactNode
}) {
  return (
    <div className="filter-bar">
      {children}
      {actions ? <div className="filter-bar__actions">{actions}</div> : null}
    </div>
  )
}

/**
 * 고르는 칩 묶음. 드롭다운과 달리 무엇을 고를 수 있는지 한눈에 보인다.
 * `value === null` 이 "전체"다.
 */
export function ChipGroup<T extends string>({
  value,
  options,
  onChange,
  allLabel,
  label,
}: {
  value: T | null
  options: Array<{ value: T; label: string }>
  onChange: (value: T | null) => void
  allLabel: string
  label: string
}) {
  return (
    <div className="chip-group" role="group" aria-label={label}>
      <button
        type="button"
        className={value === null ? "chip is-on" : "chip"}
        aria-pressed={value === null}
        onClick={() => onChange(null)}
      >
        {allLabel}
      </button>
      {options.map((option) => {
        const on = option.value === value
        return (
          <button
            key={option.value}
            type="button"
            className={on ? "chip is-on" : "chip"}
            aria-pressed={on}
            // 눌린 칩을 다시 누르면 전체로 돌아간다.
            onClick={() => onChange(on ? null : option.value)}
          >
            {option.label}
          </button>
        )
      })}
    </div>
  )
}

/** 지금 걸려 있는 조건을 지울 수 있는 칩으로 보여 준다. */
export function FilterChips({
  chips,
  onClearAll,
  clearAllLabel,
  removeLabel,
}: {
  chips: Array<{ key: string; label: ReactNode; onRemove: () => void }>
  onClearAll?: () => void
  clearAllLabel: string
  removeLabel: string
}) {
  if (chips.length === 0) return null
  return (
    <div className="filter-chips">
      {chips.map((chip) => (
        <span key={chip.key} className="chip chip--applied">
          {chip.label}
          <button
            type="button"
            className="chip__x"
            onClick={chip.onRemove}
            aria-label={`${removeLabel}`}
          >
            <Icon name="close" size={12} />
          </button>
        </span>
      ))}
      {onClearAll && chips.length > 1 ? (
        <button type="button" className="chip chip--clear" onClick={onClearAll}>
          {clearAllLabel}
        </button>
      ) : null}
    </div>
  )
}
